import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useChartColors } from '@/lib/hooks/useChartColors';
import { buildSeries, counterToRate, deltaToRate, pickRateUnit, summarizeSeries } from '@/lib/chartTheme';
import type { AggregatedHistoryPoint } from '@/types/app';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export interface HistoryPoint {
  collected_at: string;
  cpu_percent: number;
  memory_percent: number;
  network_in_bytes: number;
  network_out_bytes: number;
}

/** 'counter': network bytes are cumulative counters; 'delta': each point is the bytes transferred in its bucket. */
export type NetworkMode = 'counter' | 'delta';

interface StatsChartsProps {
  history: AggregatedHistoryPoint[];
  /** App stats are per-bucket deltas (default); stack stats are cumulative counters. */
  networkMode?: NetworkMode;
}

export function StatsCharts({ history, networkMode = 'delta' }: StatsChartsProps) {
  return <HistoryCharts history={history} networkMode={networkMode} />;
}

export function HistoryCharts({ history, networkMode }: { history: HistoryPoint[]; networkMode: NetworkMode }) {
  const colors = useChartColors();

  if (!history || history.length === 0) {
    return <div className="py-8 text-center text-muted-foreground">No historical data available</div>;
  }

  const labels = history.map((h) => new Date(h.collected_at).toLocaleTimeString());
  const times = history.map((h) => new Date(h.collected_at).getTime());
  const cpu = history.map((h) => h.cpu_percent);
  const mem = history.map((h) => h.memory_percent);
  const toRate = networkMode === 'counter' ? counterToRate : deltaToRate;
  const rawIn = toRate(history.map((h) => h.network_in_bytes), times);
  const rawOut = toRate(history.map((h) => h.network_out_bytes), times);
  const { unit, divisor } = pickRateUnit([...rawIn, ...rawOut]);
  const netIn = rawIn.map((v) => v / divisor);
  const netOut = rawOut.map((v) => v / divisor);

  const axis = (title?: string) => ({
    beginAtZero: true,
    border: { color: colors.border },
    grid: { color: colors.border },
    ticks: { color: colors.muted },
    title: title ? { display: true, text: title, color: colors.muted } : undefined,
  });

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { position: 'top' as const, labels: { color: colors.foreground } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
  };
  const xScale = { grid: { color: colors.border }, border: { color: colors.border }, ticks: { color: colors.muted, maxTicksLimit: 8 } };
  const percentOptions = { ...baseOptions, scales: { x: xScale, y: { ...axis('%'), max: 100 } } };
  const cpuOptions = { ...baseOptions, scales: { x: xScale, y: axis('%') } };
  const netOptions = { ...baseOptions, scales: { x: xScale, y: axis(unit) } };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="h-[300px] rounded-md border p-4">
        <h3 className="mb-2 font-semibold">CPU Usage</h3>
        <div className="h-[240px]">
          <Line
            role="img"
            aria-label={summarizeSeries('CPU %', cpu, '%')}
            options={cpuOptions}
            data={{ labels, datasets: [buildSeries('CPU %', colors.series[0], cpu)] }}
          />
        </div>
      </div>
      <div className="h-[300px] rounded-md border p-4">
        <h3 className="mb-2 font-semibold">Memory Usage</h3>
        <div className="h-[240px]">
          <Line
            role="img"
            aria-label={summarizeSeries('Memory %', mem, '%')}
            options={percentOptions}
            data={{ labels, datasets: [buildSeries('Memory %', colors.series[1], mem)] }}
          />
        </div>
      </div>
      <div className="h-[300px] rounded-md border p-4 md:col-span-2">
        <h3 className="mb-2 font-semibold">Network I/O</h3>
        <div className="h-[240px]">
          <Line
            role="img"
            aria-label={`${summarizeSeries('Network in', netIn, unit)}. ${summarizeSeries('Network out', netOut, unit)}`}
            options={netOptions}
            data={{
              labels,
              datasets: [buildSeries(`In (${unit})`, colors.series[2], netIn), buildSeries(`Out (${unit})`, colors.series[3], netOut)],
            }}
          />
        </div>
      </div>
    </div>
  );
}
